/**
 * Platform detection utilities for tailoring native-app promotion.
 *
 * Extracted from the (removed) PWAInstallPrompt so the checks live in one
 * place. All functions are SSR-safe — they guard `navigator`/`window`
 * before touching them.
 */

/** True on iPhone/iPad/iPod, including iPadOS that masquerades as desktop. */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+ reports as `MacIntel` but exposes a touch screen.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/** True on Android devices. */
export function isAndroid(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent);
}

/** True on any mobile platform we promote the native app for. */
export function isMobile(): boolean {
  return isIOS() || isAndroid();
}

/**
 * True when running as an installed PWA (standalone display mode, or
 * iOS Safari's legacy `navigator.standalone`). These users are already
 * inside an app shell, so we never steer them toward the store.
 */
export function isStandalonePWA(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in window.navigator &&
      (window.navigator as Navigator & { standalone?: boolean }).standalone ===
        true)
  );
}

/**
 * True in a non-Safari browser on iPhone/iPad (Chrome, Firefox, Edge,
 * Opera). Safari on iOS shows the Smart App Banner itself, so only the
 * other browsers need our own App Store nudge.
 */
export function isIOSNonSafari(): boolean {
  if (!isIOS()) return false;
  return /CriOS|FxiOS|EdgiOS|OPiOS|OPT\//.test(navigator.userAgent);
}
